import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  ChevronDown,
  ChevronRight,
  Info,
  Save,
  Shield,
} from "lucide-react";
import { ipDocketApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import { getJurisdiction } from "./jurisdictionConfig";

const CATEGORY_STYLES: Record<string, { bg: string; text: string }> = {
  filing: { bg: "bg-blue-100", text: "text-blue-700" },
  prosecution: { bg: "bg-purple-100", text: "text-purple-700" },
  maintenance: { bg: "bg-amber-100", text: "text-amber-700" },
  opposition: { bg: "bg-red-100", text: "text-red-700" },
  validation: { bg: "bg-indigo-100", text: "text-indigo-700" },
  other: { bg: "bg-slate-100", text: "text-slate-600" },
};

const PRIORITY_STYLES: Record<string, { bg: string; text: string }> = {
  critical: { bg: "bg-red-100", text: "text-red-700" },
  high: { bg: "bg-amber-100", text: "text-amber-700" },
  medium: { bg: "bg-blue-100", text: "text-blue-700" },
  low: { bg: "bg-slate-100", text: "text-slate-600" },
};

// ─── Types ─────────────────────────────────────────────────────────────────

interface DeadlineRule {
  _id: string;
  jurisdiction: string;
  ipType: string;
  triggerEvent: string;
  deadlineName: string;
  description?: string;
  offsetMonths?: number;
  offsetDays?: number;
  offsetYears?: number;
  gracePeriodMonths?: number;
  gracePeriodDays?: number;
  isExtendable?: boolean;
  maxExtensionMonths?: number;
  category?: string;
  priority?: string;
  feeInfo?: string;
  isSystemRule?: boolean;
}

interface RulePreferences {
  disabledRuleIds: string[];
  customReminders: { ruleId: string; extraReminderDays: number[] }[];
}

interface GroupKey {
  jurisdiction: string;
  ipType: string;
}

interface IPRulesSettingsProps {
  open: boolean;
  onClose: () => void;
}

// ─── Toggle Switch ─────────────────────────────────────────────────────────

const ToggleSwitch: React.FC<{
  enabled: boolean;
  onChange: (val: boolean) => void;
}> = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      enabled ? "bg-[#0F5132]" : "bg-slate-300"
    }`}
  >
    <motion.span
      layout
      transition={{ type: "spring", stiffness: 700, damping: 30 }}
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

// ─── Offset description ────────────────────────────────────────────────────

function formatOffset(rule: DeadlineRule): string {
  const parts: string[] = [];
  if (rule.offsetYears) parts.push(`${rule.offsetYears} year${rule.offsetYears > 1 ? "s" : ""}`);
  if (rule.offsetMonths) parts.push(`${rule.offsetMonths} month${rule.offsetMonths > 1 ? "s" : ""}`);
  if (rule.offsetDays) parts.push(`${rule.offsetDays} day${rule.offsetDays > 1 ? "s" : ""}`);
  if (parts.length === 0) return "";

  const triggerLabel = rule.triggerEvent.replace(/_/g, " ");
  let text = `${parts.join(", ")} from ${triggerLabel}`;

  if (rule.isExtendable && rule.maxExtensionMonths) {
    const total = (rule.offsetMonths || 0) + rule.maxExtensionMonths;
    text += ` (extendable to ${total} months)`;
  }

  return text;
}

function formatGracePeriod(rule: DeadlineRule): string {
  const parts: string[] = [];
  if (rule.gracePeriodMonths) parts.push(`${rule.gracePeriodMonths} month${rule.gracePeriodMonths > 1 ? "s" : ""}`);
  if (rule.gracePeriodDays) parts.push(`${rule.gracePeriodDays} day${rule.gracePeriodDays > 1 ? "s" : ""}`);
  if (parts.length === 0) return "";
  return `${parts.join(", ")} grace period`;
}

function formatIpType(t: string): string {
  return t.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// ─── Component ─────────────────────────────────────────────────────────────

const IPRulesSettings: React.FC<IPRulesSettingsProps> = ({ open, onClose }) => {
  const [rules, setRules] = useState<DeadlineRule[]>([]);
  const [prefs, setPrefs] = useState<RulePreferences>({ disabledRuleIds: [], customReminders: [] });
  const [originalPrefs, setOriginalPrefs] = useState<RulePreferences>({ disabledRuleIds: [], customReminders: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, prefsRes] = await Promise.all([
        ipDocketApi.rules(),
        ipDocketApi.preferences(),
      ]);
      const rulesData = rulesRes.data?.data || [];
      setRules(rulesData);

      const prefsData = prefsRes.data?.data || { disabledRuleIds: [], customReminders: [] };
      const normalizedPrefs: RulePreferences = {
        disabledRuleIds: (prefsData.disabledRuleIds || []).map((id: any) => String(id)),
        customReminders: prefsData.customReminders || [],
      };
      setPrefs(normalizedPrefs);
      setOriginalPrefs(normalizedPrefs);

      // Expand the first group by default
      if (rulesData.length > 0) {
        const firstKey = `${rulesData[0].jurisdiction}:${rulesData[0].ipType}`;
        setExpandedGroups(new Set([firstKey]));
      }
    } catch (err) {
      console.error("Failed to fetch rules/preferences:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  // ─── Group rules by jurisdiction + ipType ──────────────────────────────

  const grouped = useMemo(() => {
    const map = new Map<string, { key: GroupKey; rules: DeadlineRule[] }>();
    for (const rule of rules) {
      const gKey = `${rule.jurisdiction}:${rule.ipType}`;
      if (!map.has(gKey)) {
        map.set(gKey, { key: { jurisdiction: rule.jurisdiction, ipType: rule.ipType }, rules: [] });
      }
      map.get(gKey)!.rules.push(rule);
    }
    return Array.from(map.values());
  }, [rules]);

  // ─── Dirty check ──────────────────────────────────────────────────────

  const hasChanges = useMemo(() => {
    const origArr = originalPrefs.disabledRuleIds.slice().sort();
    const currArr = prefs.disabledRuleIds.slice().sort();
    if (origArr.length !== currArr.length) return true;
    for (let i = 0; i < origArr.length; i++) {
      if (origArr[i] !== currArr[i]) return true;
    }
    return false;
  }, [originalPrefs, prefs]);

  // ─── Toggle handlers ─────────────────────────────────────────────────

  const isRuleEnabled = (ruleId: string) => !prefs.disabledRuleIds.includes(ruleId);

  const toggleRule = (ruleId: string) => {
    setPrefs((prev) => {
      const disabled = new Set(prev.disabledRuleIds);
      if (disabled.has(ruleId)) {
        disabled.delete(ruleId);
      } else {
        disabled.add(ruleId);
      }
      return { ...prev, disabledRuleIds: Array.from(disabled) };
    });
  };

  const enableAll = (groupRules: DeadlineRule[]) => {
    setPrefs((prev) => {
      const disabled = new Set(prev.disabledRuleIds);
      for (const r of groupRules) disabled.delete(r._id);
      return { ...prev, disabledRuleIds: Array.from(disabled) };
    });
  };

  const disableAll = (groupRules: DeadlineRule[]) => {
    setPrefs((prev) => {
      const disabled = new Set(prev.disabledRuleIds);
      for (const r of groupRules) disabled.add(r._id);
      return { ...prev, disabledRuleIds: Array.from(disabled) };
    });
  };

  const toggleGroup = (gKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gKey)) next.delete(gKey);
      else next.add(gKey);
      return next;
    });
  };

  // ─── Save ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      await ipDocketApi.updatePreferences({
        disabledRuleIds: prefs.disabledRuleIds,
        customReminders: prefs.customReminders,
      });
      setOriginalPrefs({ ...prefs });
    } catch (err) {
      console.error("Failed to save preferences:", err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Deadline Rules"
      subtitle="Configure which deadline rules are active for your firm"
      icon={Settings}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Info className="h-3.5 w-3.5" />
            <span>
              Disabling a rule means new deadlines won't be computed for that
              rule. Existing docket entries are not affected.
            </span>
          </div>
          <AnimatePresence>
            {hasChanges && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50 flex-shrink-0 ml-4"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#146C43")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0F5132")
                }
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving..." : "Save Changes"}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div
            className="animate-spin h-8 w-8 rounded-full border-4"
            style={{
              borderColor: "#0F5132",
              borderTopColor: "transparent",
            }}
          />
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Shield className="h-7 w-7 text-slate-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            No Rules Available
          </h3>
          <p className="text-sm text-slate-500">
            Deadline rules haven't been configured yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Info banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-2">
            <p className="text-sm text-emerald-800">
              System rules are maintained by Kavoa and updated when patent
              office procedures change. Toggle rules on or off to control which
              deadlines are automatically computed for your firm.
            </p>
          </div>

          {/* Grouped sections */}
          {grouped.map((group, groupIdx) => {
            const gKey = `${group.key.jurisdiction}:${group.key.ipType}`;
            const isExpanded = expandedGroups.has(gKey);
            const activeCount = group.rules.filter(
              (r) => !prefs.disabledRuleIds.includes(r._id)
            ).length;
            const j = getJurisdiction(group.key.jurisdiction);

            return (
              <div
                key={gKey}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                {/* Section header */}
                <button
                  onClick={() => toggleGroup(gKey)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </motion.div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg" style={{ background: j.gradient }}>
                      <i className={j.icon} style={{ color: j.iconColor, fontSize: "14px" }} />
                      <span className="font-semibold text-sm" style={{ color: j.iconColor }}>{j.name}</span>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {formatIpType(group.key.ipType)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Enable/Disable All */}
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => enableAll(group.rules)}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-800 transition-colors"
                      >
                        Enable All
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        onClick={() => disableAll(group.rules)}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Disable All
                      </button>
                    </div>
                    <span className="text-sm text-slate-500">
                      {group.rules.length} rule{group.rules.length !== 1 ? "s" : ""}{" "}
                      <span
                        className={
                          activeCount === group.rules.length
                            ? "text-emerald-600 font-medium"
                            : "text-amber-600 font-medium"
                        }
                      >
                        ({activeCount} active)
                      </span>
                    </span>
                  </div>
                </button>

                {/* Expanded rules */}
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-slate-100 divide-y divide-slate-100">
                        {group.rules.map((rule, rIdx) => {
                          const enabled = isRuleEnabled(rule._id);
                          const catStyle =
                            CATEGORY_STYLES[rule.category || "other"] ||
                            CATEGORY_STYLES.other;
                          const priStyle =
                            PRIORITY_STYLES[rule.priority || "medium"] ||
                            PRIORITY_STYLES.medium;
                          const offsetText = formatOffset(rule);
                          const graceText = formatGracePeriod(rule);

                          return (
                            <motion.div
                              key={rule._id}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: rIdx * 0.03 }}
                              className={`px-5 py-4 flex items-start gap-4 transition-opacity ${
                                enabled ? "opacity-100" : "opacity-50"
                              }`}
                            >
                              {/* Toggle */}
                              <div className="pt-0.5 flex-shrink-0">
                                <ToggleSwitch
                                  enabled={enabled}
                                  onChange={() => toggleRule(rule._id)}
                                />
                              </div>

                              {/* Rule details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-semibold text-sm text-slate-900">
                                    {rule.deadlineName}
                                  </span>
                                  {rule.isExtendable && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
                                      Extendable
                                    </span>
                                  )}
                                </div>

                                {/* Offset description */}
                                {(offsetText || rule.description) && (
                                  <p className="text-xs text-slate-500 mb-2">
                                    {rule.description || offsetText}
                                  </p>
                                )}

                                {/* Badges row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {rule.category && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${catStyle.bg} ${catStyle.text}`}
                                    >
                                      {rule.category}
                                    </span>
                                  )}
                                  {rule.priority && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${priStyle.bg} ${priStyle.text}`}
                                    >
                                      {rule.priority}
                                    </span>
                                  )}
                                  {graceText && (
                                    <span className="text-[10px] text-slate-400 font-medium">
                                      {graceText}
                                    </span>
                                  )}
                                  {rule.feeInfo && (
                                    <span className="text-[10px] text-slate-400 italic">
                                      {rule.feeInfo}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {/* Footer note */}
          <div className="text-center pt-2 pb-1">
            <p className="text-xs text-slate-400">
              Rules are maintained by Kavoa and automatically updated when
              patent office procedures change. Last updated: April 2026.
            </p>
          </div>
        </div>
      )}
    </PremiumModal>
  );
};

export default IPRulesSettings;
